import {
  AutomaticStatusWaiting,
  TimestampStatusCreating,
} from '@/features/invoice_reports/types';
import { usePaymentRequestSupportAgentFlag } from '@/hooks/feature_flag/usePaymentRequestSupportAgentFlag';
import { useTranslation } from '@/i18n';
import { IconPending } from '@moneyforward/ap-frontend-components';
import { ReactNode, useMemo } from 'react';

export type AutomaticStatusTag = {
  label: string;
  status: 'success' | 'warning' | 'error' | 'default';
  key: string;
  icon?: ReactNode;
};

export type UseAutomaticStatusTagsProps = {
  automaticStatusWaiting?: AutomaticStatusWaiting;
  timestampStatusCreating?: TimestampStatusCreating;
};

export const useAutomaticStatusTags = ({
  automaticStatusWaiting,
  timestampStatusCreating,
}: UseAutomaticStatusTagsProps) => {
  const { t } = useTranslation();
  const isAiSuggestEnabled = usePaymentRequestSupportAgentFlag();
  const statusTags = useMemo(() => {
    const tags: AutomaticStatusTag[] = [];

    // Handle timestamp status - when timestamp_status_creating is creating, show tag タイムスタンプ付与中
    if (timestampStatusCreating === TimestampStatusCreating.CREATING) {
      tags.push({
        label: t('label_status_timestamp_creating'),
        status: 'success',
        key: 'timestamp_creating',
      });
    }
    if (automaticStatusWaiting) {
      switch (automaticStatusWaiting) {
        case AutomaticStatusWaiting.BEFORE_FLAIR_UPLOAD:
        case AutomaticStatusWaiting.FLAIR_UPLOADED:
        case AutomaticStatusWaiting.BEFORE_OCR_UPLOAD:
        case AutomaticStatusWaiting.OCR_UPLOADED:
          tags.push({
            label: t('label_status_ocr_reading'),
            status: 'success',
            key: 'ocr_reading',
          });
          break;
        case AutomaticStatusWaiting.BEFORE_STREAMED_UPLOAD:
        case AutomaticStatusWaiting.STREAMED_UPLOADED:
          tags.push({
            label: t('label_status_operator_input'),
            status: 'success',
            key: 'operator_input',
          });
          break;
        case AutomaticStatusWaiting.BEFORE_AI_SUGGEST:
        case AutomaticStatusWaiting.AI_SUGGESTED:
          // AI Suggest processing label is only shown when the flag is on (NA-5312).
          if (isAiSuggestEnabled) {
            tags.push({
              label: t('label_status_ai_suggest_processing'),
              status: 'success',
              key: 'ai_suggest_processing',
              icon: <IconPending size={12} />,
            });
          }
          break;
        default:
          break;
      }
    }
    return tags;
  }, [automaticStatusWaiting, timestampStatusCreating, isAiSuggestEnabled, t]);

  return {
    statusTags,
  };
};
